import { useState, useEffect } from "react"
import clsx from "clsx"
import ReactStars from "react-rating-stars-component"
import { useRouter } from "next/router"
import { toast } from "react-toastify"
import { useTranslation } from "i18n"
import metadata from "metadata.json"

// Material UI
import { lighten } from "@material-ui/core/styles/colorManipulator"
import { makeStyles, fade } from "@material-ui/core/styles"
import {
  Grid,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress
} from "@material-ui/core"

// Components
import Button from "components/shared/Button"
import StoreAppBar from "components/store/StoreAppBar"
import KnowledgeGainedCard from "components/store/shared/KnowledgeGainedCard"
import SkillsObtainedCard from "components/store/shared/SkillsObtainedCard"
import KeyHighlightsList from "components/store/shared/KeyHighlightsList"
import AboutItem from "components/store/shared/AboutItem"
import ItemCertificate from "components/store/shared/ItemCertificate"
import FAQ from "components/store/shared/FAQ"
import LearningModes from "components/store/shared/LearningModes"
import MilestonePanel from "components/store/shared/MilestonePanel"
import Footer from "components/landing_page/Footer"
import LoadingScreen from "components/globals/LoadingScreen"
import { GA, GA_EVENTS } from "utils/googleAnalytics"

// Icons
import PlayArrowIcon from "@material-ui/icons/PlayArrow"

// Helpers
import axios from "shared/axios"
import urls from "shared/urls"
import {
  getAuthInfo,
  getPluralOrSingular,
  getMinimumPrice,
  handleSSO,
  getPlatformURL,
  getTrackingDetails
} from "shared/helpers"
import { addToCartV2 } from "shared/common_apis"
import Metadata from "shared/metadata"

const useStyles = makeStyles(theme => ({
  contentPadding: {
    paddingLeft: theme.spacing(20),
    paddingRight: theme.spacing(20),
    [theme.breakpoints.down("md")]: {
      paddingLeft: theme.spacing(7),
      paddingRight: theme.spacing(7)
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5)
    },
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    },
    transition: theme.transitions.create("padding", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  banner: {
    padding: theme.spacing(13, 20),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(13, 7)
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 5)
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(7, 2)
    },
    backgroundColor: fade(theme.palette.common.black, 0.8),
    color: theme.palette.common.white
  },

  iconButtonRoot: {
    marginRight: theme.spacing(2),
    padding: theme.spacing(0.8),
    color: theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    "&:hover": {
      backgroundColor: fade(theme.palette.common.white, 0.8)
    }
  },
  navItem: {
    padding: theme.spacing(2.5, 4),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 3)
    }
  },
  tabActive: {
    borderBottom: `4px solid ${theme.palette.primary.main}`
  },

  // Buttons
  enrollBtn: {
    backgroundImage: theme.gradients.primaryButton,
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 8),
    boxShadow: theme.shadows[25],
    "&:disabled": {
      color: theme.palette.common.white
    }
  },

  // Background Colors/Gradients
  syllabus: {
    backgroundImage: theme.gradients.bluePurple,
    opacity: 0.9
  },
  certificateBackground: {
    backgroundColor: lighten(theme.palette.primary.main, 0.88)
  },
  footer: {
    backgroundColor: theme.palette.grey.aluminium
  },

  downloadBrochureBtn: {
    padding: theme.spacing(1, 5)
  },

  // Helper Classes
  textWhite: {
    color: theme.palette.common.white
  }
}))

// Tab Choices
const ABOUT_TAB = "ABOUT_TAB"
const FAQ_TAB = "FAQ_TAB"
const REVIEWS_TAB = "REVIEWS_TAB"
const SYLLABUS_TAB = "SYLLABUS_TAB"

const COMPARISON_TABLE = "compareLearningModes"

const navTabs = [
  {
    display: "About",
    id: ABOUT_TAB
  },
  {
    display: "Syllabus",
    id: SYLLABUS_TAB
  },
  {
    display: "Reviews",
    id: REVIEWS_TAB
  },
  {
    display: "FAQ",
    id: FAQ_TAB
  }
]

export default function CourseDetails(props) {
  const classes = useStyles()
  const { t } = useTranslation()
  const { slug } = props

  const theme = useTheme()
  const xsDown = useMediaQuery(theme.breakpoints.down("xs"))

  const { query, asPath } = useRouter()
  const courseSlug = query.courseSlug

  const [buyLoading, setBuyLoading] = useState(false)
  const [userInfo, setUserInfo] = useState(null)
  const [course, setCourse] = useState(null)
  const [milestones, setMilestones] = useState([])
  const [tab, setTab] = useState(ABOUT_TAB)
  const [taxesIncluded, setTaxesIncluded] = useState(false)

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const getCourseDetails = () => {
      axios
        .get(urls.store.courses.details(courseSlug))
        .then(response => {
          setCourse(response.data.data)
          GA.trackEvent(GA_EVENTS.view_item, {
            items: [
              {
                item_id: response.data.data.uuid,
                item_category: "course",
                item_name: response.data.data.name
              }
            ]
          })
          setTaxesIncluded(
            getMinimumPrice(response.data.data.products).inclusive_of_tax
          )
          setLoading(false)
        })
        .catch(console.error)
    }

    const getCourseMilestones = () => {
      axios
        .get(urls.store.courses.milestones(courseSlug))
        .then(response => {
          setMilestones(response.data.data)
        })
        .catch(console.error)
    }

    // const getCourseReviews = () => {
    //   axios.get(urls.store.courses.reviews(courseSlug)).then(response => {
    //     setReviews(response.data.data.data)
    //   })
    // }

    setUserInfo(getAuthInfo())

    if (courseSlug) {
      getCourseDetails()
      getCourseMilestones()
    }
  }, [courseSlug])

  const handleEnrollmentClick = () => {
    const el = document.getElementById(COMPARISON_TABLE)
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" })
    } else {
      handlePurchase()
    }
  }

  const handlePurchase = productId => {
    if (userInfo === null) {
      handleSSO()
      return
    }
    setBuyLoading(true)
    const { products } = course
    productId = productId || products[0].id

    addToCartV2(productId, products[0].centres)
      .then(() => {
        setBuyLoading(false)
        toast.success("Added to cart")
        GA.trackEvent(
          GA_EVENTS.add_to_cart,
          getTrackingDetails(course, {
            type: "course",
            productId
          })
        )
        window.location = getPlatformURL("learn", "/orders/payment/")
      })
      .catch(error => {
        if (error.response) {
          const response = error.response.data
          if (response.errors) {
            toast.error(response.errors.message)
          }
        }
        setBuyLoading(false)
      })
  }

  const changeTab = tab => {
    const el = document.getElementById(tab)
    if (el) {
      el.scrollIntoView()
      setTab(tab)
    }
  }

  const formatPrice = products => {
    const { price, mrp, currency } = getMinimumPrice(products)

    if (mrp === price) {
      return (
        <span>
          {currency.symbol}
          {price}
        </span>
      )
    }
    return (
      <Box component="span" mr={1}>
        <s>
          {currency.symbol}
          {mrp}
        </s>
        <Box component="span" ml={1}>
          {currency.symbol}
          {price}
        </Box>
      </Box>
    )
  }

  const getMetaData = () => {
    const data = metadata.courseDetails[slug]
    return {
      ...data,
      title: data?.title || course?.name,
      description: data?.description || course?.course_attributes.tagline
    }
  }

  return (
    <>
      <Metadata data={getMetaData()}>
        {course && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                image: course.image ?? "",
                url: `https://www.suraasa.com/store${asPath}`,
                name: course.name,
                description: course.description ?? "",
                sku: course.uuid,
                brand: {
                  "@type": "Brand",
                  name: "Suraasa"
                },
                offers: {
                  "@type": "Offer",
                  availability: "https://schema.org/InStock",
                  priceCurrency: `${
                    getMinimumPrice(course.products).currency.code
                  }`,
                  price: `${getMinimumPrice(course.products).price}`
                }
              })
            }}
          />
        )}
      </Metadata>

      <StoreAppBar />

      {loading ? (
        <LoadingScreen />
      ) : (
        <div>
          <Box id={ABOUT_TAB}>
            <div
              style={{
                height: "100%",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundImage: "url(/assets/images/course_banner.jpg)"
              }}
            >
              <div className={clsx(classes.banner, classes.contentPadding)}>
                <Grid container direction="column">
                  <Grid item>
                    <Grid container spacing={3} direction="column">
                      <Grid item md={8}>
                        <Typography
                          variant="h3"
                          className="text-medium"
                          component="h1"
                        >
                          {course.name}
                        </Typography>
                        <Typography variant="h5" component="h2">
                          {course.course_attributes.tagline}
                        </Typography>
                      </Grid>

                      {/* {course.rating === 0 && ( */}
                      <Grid item>
                        <div className="flex align-items-center">
                          <ReactStars
                            count={5}
                            edit={false}
                            // value={course.rating}
                            value={5}
                            half={true}
                            size={30}
                          />
                          <Box pt={1} ml={1}>
                            <Typography variant="h6">
                              {getPluralOrSingular("Star", 5)}
                            </Typography>
                          </Box>
                        </div>
                      </Grid>
                      {/* )} */}

                      {course.products ? (
                        <Grid item>
                          <Box py={2}>
                            <Grid container spacing={1} alignItems="flex-end">
                              {course.products.length > 1 && (
                                <Grid item>
                                  <Typography variant="h5">
                                    {t("Starting")}:
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item>
                                <Typography variant="h4">
                                  {formatPrice(course.products)}
                                </Typography>
                              </Grid>
                              <Grid item>
                                {taxesIncluded ? (
                                  <Typography variant="body1">
                                    {t("(Including Taxes)")}
                                  </Typography>
                                ) : (
                                  <Typography variant="body1">
                                    {t("(Excluding Taxes)")}
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      ) : (
                        <Box my={2} />
                      )}

                      <Grid item>
                        <Grid container spacing={4} alignItems="center">
                          <Grid item>
                            <Grid
                              container
                              direction="column"
                              alignItems="center"
                            >
                              <Grid item>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  disabled={
                                    course.is_enrolled ||
                                    !course.products ||
                                    buyLoading
                                  }
                                  onClick={handleEnrollmentClick}
                                  className={classes.enrollBtn}
                                >
                                  <Typography variant="body1">
                                    {course.is_enrolled
                                      ? t("Enrolled")
                                      : course.products
                                      ? t("Enroll Now")
                                      : t("Not Available")}
                                  </Typography>
                                  {buyLoading && (
                                    <CircularProgress
                                      color="white"
                                      size="20px"
                                      style={{
                                        marginLeft: "12px"
                                      }}
                                    />
                                  )}
                                </Button>
                              </Grid>
                              {course.number_of_enrollments > 300 && (
                                <Grid item>
                                  <Typography variant="body2">
                                    {getPluralOrSingular(
                                      "Learner",
                                      course.number_of_enrollments
                                    )}
                                    &nbsp;Enrolled
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                          {course.is_enrolled && (
                            <Grid item>
                              <Box mb={{ sm: 2 }}>
                                <IconButton
                                  size="small"
                                  classes={{
                                    root: classes.iconButtonRoot
                                  }}
                                  component="a"
                                  href={getPlatformURL(
                                    "learn",
                                    `/course/${course.slug}`
                                  )}
                                >
                                  <PlayArrowIcon
                                    fontSize="large"
                                    color="primary"
                                  />
                                </IconButton>

                                <Typography variant="body1" display="inline">
                                  {course.is_enrolled
                                    ? "Continue Learning"
                                    : "Demo this course"}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </div>
          </Box>

          <Box bgcolor="white" className={classes.contentPadding}>
            <Grid container>
              {navTabs.map(item => {
                // Don't render reviews tab if there are no course reviews
                if (item.id === REVIEWS_TAB) return null
                return (
                  <Grid
                    item
                    key={item.id}
                    className={clsx(classes.navItem, {
                      [classes.tabActive]: tab === item.id
                    })}
                  >
                    <Typography
                      variant="body1"
                      className="cursor-pointer text-bold"
                      color={tab === item.id ? "primary" : "inherit"}
                      onClick={() => changeTab(item.id)}
                    >
                      {item.display}
                    </Typography>
                  </Grid>
                )
              })}
            </Grid>
          </Box>

          <Box my={5} className={classes.contentPadding}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Grid container direction="column" spacing={3}>
                  <Grid item>
                    <Grid container direction="column" spacing={2}>
                      <Grid item>
                        <KnowledgeGainedCard
                          items={
                            course.course_attributes.marketing_learning_outcomes
                          }
                        />
                      </Grid>
                      <Grid item>
                        <SkillsObtainedCard
                          items={course.course_attributes.skills}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <AboutItem type="course" description={course.description} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <KeyHighlightsList
                  type="course"
                  prices={course.products}
                  duration={course.duration}
                  available_countries={course.available_countries}
                  language={course.available_languages}
                  level={course.level}
                />
              </Grid>
            </Grid>
          </Box>

          <Box id={SYLLABUS_TAB} py={10} className={classes.syllabus}>
            <Box className={classes.contentPadding}>
              <Grid container direction="column" spacing={3}>
                <Grid item container alignItems="center">
                  <Grid
                    item
                    xs={12}
                    sm={8}
                    md={9}
                    xl={10}
                    className={classes.textWhite}
                  >
                    <Typography variant="h4">
                      {t("Included Courses & Studies")}
                    </Typography>
                    <Typography variant="h6">
                      {t(
                        "Following modules are covered in our mentor-led classes and self-paced learning systems on Suraasa"
                      )}
                      :
                    </Typography>
                  </Grid>
                  {course.syllabus &&
                  course.syllabus !== "/" &&
                  course.syllabus !== "\\" ? (
                    <Grid item xs={12} sm="auto" className={classes.textWhite}>
                      <a
                        href={course.syllabus}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Box mt={{ xs: 2, md: 0 }}>
                          <Button
                            fullWidth={xsDown}
                            variant="contained"
                            color="default"
                            className={classes.downloadBrochureBtn}
                          >
                            <Typography variant="body1">
                              {t("Download Brochure")}
                            </Typography>
                          </Button>
                        </Box>
                      </a>
                    </Grid>
                  ) : null}
                </Grid>
                <Grid item>
                  <Grid container direction="column" spacing={2}>
                    {milestones.map(milestone => (
                      <Grid item key={milestone.name}>
                        <MilestonePanel milestone={milestone} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box py={10} className={classes.certificateBackground}>
            <Box className={classes.contentPadding}>
              <ItemCertificate
                handleClick={handleEnrollmentClick}
                is_enrolled={course.is_enrolled}
                number_of_enrollments={course.number_of_enrollments}
                buyLoading={buyLoading}
              />
            </Box>
          </Box>

          <Box id={FAQ_TAB} py={10} className={classes.contentPadding}>
            <FAQ />
          </Box>

          {course.products.length > 1 && (
            <LearningModes
              prices={course.products}
              features={course.course_attributes.learning_mode_features}
              handlePurchase={handlePurchase}
              isEnrolled={course.is_enrolled}
              buyLoading={buyLoading}
            />
          )}
        </div>
      )}

      <Footer />
    </>
  )
}

export async function getServerSideProps(context) {
  return {
    props: { slug: context.query.courseSlug }
  }
}
